import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ScrollSnapWheel from './ScrollSnapWheel'
import { SpecShieldIcon } from './ListingSpecIcons'
import {
  WARRANTY_REMAINING_ORDER,
  type WarrantyRemainingKey,
  isWarrantyRemainingKey,
} from '../utils/warrantyRemaining'

const WARRANTY_ACCENT = '#48a0b8'

type ValueChipProps = {
  value: string
  ariaLabel?: string
}

export function WarrantyRemainingValueChip({
  value,
  ariaLabel = 'Warranty remaining',
}: ValueChipProps) {
  const { t } = useTranslation()
  const known = isWarrantyRemainingKey(value)
  const tone = known ? WARRANTY_ACCENT : '#94a3b8'
  const selectedLabel = known ? t(`listing.warrantyRemaining.${value}`) : null

  return (
    <span
      className={`listing-spec-value-chip listing-spec-value-chip--battery warranty-remaining-value-chip${
        known ? '' : ' listing-spec-value-chip--muted warranty-remaining-value-chip--empty'
      }`}
      style={known ? { color: tone, borderColor: `${tone}55` } : undefined}
      aria-label={ariaLabel}
    >
      <SpecShieldIcon size={18} />
      {selectedLabel ? (
        <span className="warranty-remaining-value-chip__text">{selectedLabel}</span>
      ) : null}
    </span>
  )
}

interface WarrantyRemainingWheelPickerProps {
  value: string
  onChange: (value: WarrantyRemainingKey) => void
  compact?: boolean
  ariaLabel?: string
  showValueChip?: boolean
}

export default function WarrantyRemainingWheelPicker({
  value,
  onChange,
  compact = false,
  ariaLabel,
  showValueChip = true,
}: WarrantyRemainingWheelPickerProps) {
  const { t } = useTranslation()
  const label = ariaLabel ?? t('listing.warrantyRemainingLegend')

  const items = WARRANTY_REMAINING_ORDER
  const known = isWarrantyRemainingKey(value)
  const wheelValue: WarrantyRemainingKey = known ? value : items[0]

  const formatItem = useMemo(
    () => (key: WarrantyRemainingKey) => t(`listing.warrantyRemaining.${key}`),
    [t],
  )

  return (
    <div
      className={`battery-scroll-field warranty-remaining-scroll-field${compact ? ' battery-scroll-field--compact' : ''}`}
    >
      {showValueChip ? (
        <div className="listing-spec-cell__value warranty-remaining-chip-wrap">
          <WarrantyRemainingValueChip value={value} ariaLabel={label} />
        </div>
      ) : null}
      <ScrollSnapWheel
        className="warranty-remaining-scroll-wheel usage-duration-scroll-wheel"
        value={wheelValue}
        onChange={(key) => onChange(key)}
        items={items}
        ariaLabel={label}
        formatItem={formatItem}
        highlightActive={known}
        compact={compact}
      />
    </div>
  )
}
